import { useMemo, useState, type MouseEvent } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Connection, RummyStateData } from "../sockets-client";
import { CardDTO, PlayerInfo, toCardCode } from "../interface/GameTypes";
import { Card as CardImg } from "./Card";
import { Toast } from "./Toast";
import { ReactionMenu } from "./ReactionMenu";
import { ReactionOverlay } from "./ReactionOverlay";
import { TableGiphyOverlay } from "./TableGiphyOverlay";
import { TableGiphyPickerDialog } from "./TableGiphyPickerDialog";
import { useTableSocialSocket } from "../hooks/useTableSocialSocket";

type Props = {
  state: RummyStateData;
  myUUID: string;
  socketConnection: Connection;
  onReturnToLobby: () => void;
};

function cardKey(c: CardDTO): string {
  return `${c.suit}\u0000${c.rank}`;
}

function cardLabel(c: CardDTO): string {
  if (c.suit === "Joker") return `J${c.rank}`;
  return `${c.rank}${c.suit[0]}`;
}

function RummyCardFace({ c, selected }: { c: CardDTO; selected?: boolean }) {
  if (c.suit === "Joker") {
    return (
      <Chip
        label={`Joker ${c.rank}`}
        size="small"
        sx={{
          m: 0.25,
          minWidth: 56,
          fontWeight: "bold",
          border: selected ? "2px solid #FFD700" : undefined,
        }}
      />
    );
  }
  const { value, suit } = toCardCode(c);
  return (
    <Box
      sx={{
        display: "inline-block",
        lineHeight: 0,
        borderRadius: 1,
        outline: selected ? "3px solid #FFD700" : "none",
        outlineOffset: 2,
      }}
    >
      <CardImg value={value} suit={suit} />
    </Box>
  );
}

export const RummyTable = ({
  state,
  myUUID,
  socketConnection,
  onReturnToLobby,
}: Props) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [discardPick, setDiscardPick] = useState<CardDTO | null>(null);

  const social = useTableSocialSocket(socketConnection);

  const openReactionMenuForPlayer = (p: PlayerInfo, e: MouseEvent<HTMLElement>) => {
    if (p.isBot || p.uuid === myUUID) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    social.setReactionMenuTarget({
      playerUUID: p.uuid,
      playerName: p.name ?? "Player",
      anchorX: r.left + r.width / 2,
      anchorY: r.top,
    });
  };

  const mySeat = useMemo(
    () => state.players.findIndex((p) => p.uuid === myUUID),
    [state.players, myUUID]
  );

  const sortedHand = useMemo(() => {
    const h = [...state.myHand];
    const rankOrder = (r: string) => {
      if (r === "A") return 14;
      const base = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
      const i = base.indexOf(r);
      return i >= 0 ? i + 2 : 99;
    };
    h.sort((a, b) => {
      if (a.suit === "Joker" && b.suit !== "Joker") return 1;
      if (b.suit === "Joker" && a.suit !== "Joker") return -1;
      if (a.suit !== b.suit) return String(a.suit).localeCompare(String(b.suit));
      return rankOrder(a.rank) - rankOrder(b.rank);
    });
    return h;
  }, [state.myHand]);

  const isMyTurn =
    state.phase === "playing" && mySeat >= 0 && state.currentPlayerIndex === mySeat;
  const playerBySeat = useMemo(() => {
    const bySeat = new Map<number, PlayerInfo>();
    state.players.forEach((p) => bySeat.set(p.seatIndex, p));
    return (seatIndex: number) => bySeat.get(seatIndex);
  }, [state.players]);

  const toggleCard = (c: CardDTO) => {
    const k = cardKey(c);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const selectedCards = sortedHand.filter((c) => selectedKeys.has(cardKey(c)));

  const layMeld = () => {
    if (selectedCards.length < 3) return;
    socketConnection.rummyLayMelds([selectedCards]);
    setSelectedKeys(new Set());
  };

  const openDiscard = () => {
    if (sortedHand.length === 0) return;
    setDiscardPick(sortedHand[0]);
  };

  const confirmDiscard = () => {
    if (!discardPick) return;
    socketConnection.rummyDiscard(discardPick.suit, discardPick.rank);
    setDiscardPick(null);
  };

  const roundOver = state.phase === "round_over";
  const needInitial = mySeat >= 0 && !state.initialMeldDone[mySeat];
  const initialProgress = state.initialMeldPointsThisTurn;
  const panelSx = {
    p: 1.5,
    backgroundColor: "rgba(15,17,23,0.9)",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1200, mx: "auto" }}>
      <Paper
        elevation={6}
        sx={{
          p: 1.5,
          backgroundColor: "rgba(20,20,28,0.92)",
          color: "#f0f0f0",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Rummy Table
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => social.setGiphyPickerOpen(true)}
              disabled={social.giphyCooldownMs > 0}
            >
              GIF
            </Button>
            <Button size="small" color="inherit" onClick={onReturnToLobby}>
              Leave table
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`Stock: ${state.stockCount}`} size="small" />
          <Chip
            label={
              state.discardTop
                ? `Discard: ${cardLabel(state.discardTop)}`
                : "Discard: —"
            }
            size="small"
          />
          <Chip
            label={
              state.phase === "playing"
                ? `Turn: ${playerBySeat(state.currentPlayerIndex)?.name ?? `Seat ${state.currentPlayerIndex}`}`
                : "Round over"
            }
            size="small"
            color={isMyTurn ? "secondary" : "default"}
          />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <Paper elevation={4} sx={{ ...panelSx, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.85 }}>
              Scores
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
              {state.players.map((p, i) => (
                <Chip
                  key={p.uuid}
                  size="small"
                  label={`${p.name ?? `P${i}`}: ${state.scores[i] ?? 0}`}
                  variant={i === mySeat ? "filled" : "outlined"}
                  onClick={(e) => openReactionMenuForPlayer(p, e)}
                  sx={{
                    cursor: p.isBot || p.uuid === myUUID ? "default" : "pointer",
                  }}
                />
              ))}
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.85 }}>
              Opponents (hand size)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {state.players.map((p, i) =>
                i === mySeat ? null : (
                  <Chip
                    key={p.uuid}
                    size="small"
                    variant="outlined"
                    label={`${p.name ?? `Seat ${i}`}: ${state.handSizes[i] ?? "?"}`}
                    onClick={(e) => openReactionMenuForPlayer(p, e)}
                    sx={{
                      cursor: p.isBot || p.uuid === myUUID ? "default" : "pointer",
                    }}
                  />
                )
              )}
            </Stack>
          </Paper>

          <Paper elevation={4} sx={{ ...panelSx, flex: 1.2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.85 }}>
              Table melds
            </Typography>
            {state.melds.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  maxHeight: 220,
                  overflow: "auto",
                }}
              >
                {state.melds.map((m) => (
                  <Paper key={m.id} variant="outlined" sx={{ p: 1, backgroundColor: "rgba(0,0,0,0.35)" }}>
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5}>
                      {m.cards.map((c, idx) => (
                        <RummyCardFace key={`${m.id}-${idx}`} c={c} />
                      ))}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No melds on the table yet.
              </Typography>
            )}
          </Paper>
        </Stack>

        {isMyTurn && state.phase === "playing" && (
          <Paper sx={{ p: 1.5, my: 1.5, backgroundColor: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.35)" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Your turn
            </Typography>
            {!state.turnDrawn ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="contained" size="small" onClick={() => socketConnection.rummyDraw("stock")}>
                  Draw from stock
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={!state.discardTop}
                  onClick={() => socketConnection.rummyDraw("discard")}
                >
                  Take discard
                </Button>
              </Stack>
            ) : (
              <>
                {needInitial && initialProgress > 0 && initialProgress < 30 && (
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: "#ffb74d" }}>
                    First meld must total at least 30 points this turn before you discard ({initialProgress}/30 so far).
                  </Typography>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Button variant="contained" size="small" disabled={selectedCards.length < 3} onClick={layMeld}>
                    Lay selected meld (≥3 cards)
                  </Button>
                  <Button variant="outlined" size="small" onClick={openDiscard}>
                    Discard...
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        )}

        <Paper elevation={4} sx={panelSx}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, opacity: 0.85 }}>
            Your hand ({sortedHand.length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
            {sortedHand.map((c) => (
              <Box
                key={cardKey(c)}
                onClick={() => isMyTurn && state.turnDrawn && state.phase === "playing" && toggleCard(c)}
                sx={{
                  cursor:
                    isMyTurn && state.turnDrawn && state.phase === "playing" ? "pointer" : "default",
                  opacity: isMyTurn && state.turnDrawn && state.phase === "playing" ? 1 : 0.85,
                }}
              >
                <RummyCardFace c={c} selected={selectedKeys.has(cardKey(c))} />
              </Box>
            ))}
          </Box>
        </Paper>
      </Paper>

      {social.reactionAnnounce && (
        <Toast
          message={social.reactionAnnounce}
          severity="info"
          onClose={() => social.setReactionAnnounce(null)}
        />
      )}
      <ReactionOverlay reactions={social.reactionQueue} onComplete={social.handleReactionComplete} />
      <TableGiphyOverlay queue={social.giphyQueue} onDismissHead={social.dismissGiphyHead} />
      <TableGiphyPickerDialog
        open={social.giphyPickerOpen}
        onClose={() => social.setGiphyPickerOpen(false)}
        onPick={social.shareGiphy}
      />
      {social.reactionMenuTarget && (
        <ReactionMenu
          anchorX={social.reactionMenuTarget.anchorX}
          anchorY={social.reactionMenuTarget.anchorY}
          targetName={social.reactionMenuTarget.playerName}
          cooldownMs={social.reactionCooldownMs}
          onSelect={social.handleReactionSelect}
          onClose={social.closeReactionMenu}
        />
      )}

      <Dialog open={!!discardPick} onClose={() => setDiscardPick(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
            Choose one card to discard (ends your turn).
          </Typography>
          <Stack spacing={1}>
            {sortedHand.map((c) => (
              <Button
                key={cardKey(c)}
                variant={discardPick && cardKey(discardPick) === cardKey(c) ? "contained" : "outlined"}
                size="small"
                onClick={() => setDiscardPick(c)}
              >
                {cardLabel(c)}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardPick(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmDiscard} disabled={!discardPick}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roundOver} maxWidth="sm" fullWidth>
        <DialogTitle>{state.stalemate ? "Round drawn (stalemate)" : "Round over"}</DialogTitle>
        <DialogContent>
          {!state.stalemate && state.winnerIndex !== null && (
            <Typography sx={{ mb: 2 }}>
              Winner:{" "}
              <strong>
                {playerBySeat(state.winnerIndex)?.name ?? `Seat ${state.winnerIndex}`}
              </strong>
            </Typography>
          )}
          {state.stalemate && (
            <Typography sx={{ mb: 2 }}>
              The stock was empty. Deadwood was deducted from all players.
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Scores
          </Typography>
          <Stack spacing={0.5}>
            {state.players.map((p, i) => (
              <Typography key={p.uuid}>
                {p.name ?? `Player ${i}`}: {state.scores[i] ?? 0}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => socketConnection.playAgain(false)} variant="contained">
            Play again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
